<template>
  <div class="jim-scheduling-container pending-work-orders-container">
    <h2 class="page-heading">Pending Pablo Work Orders</h2>

    <div class="controls">
      <button @click="fetchPending" :disabled="loading">
        {{ loading ? 'Refreshing…' : 'Refresh' }}
      </button>
      <button @click="$emit('close')" class="close-btn">
        Close
      </button>
    </div>

    <div v-if="loading" class="status-msg">
      Loading pending work orders...
    </div>

    <div v-else-if="pending.length === 0" class="status-msg">
      No pending Pablo work orders for {{ location }}.
    </div>

    <div v-else class="table-wrapper">
      <table class="pending-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
            </th>
            <th>Pablo ID</th>
            <th>Ord No</th>
            <th>Item</th>
            <th>Qty</th>
            <th>Machine</th>
            
            <th>Status</th>
            <th>Source</th>
           
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in pending" :key="row.pablo_id">
            <!-- Select box -->
            <td>
              <input
                type="checkbox"
                :value="row.pablo_id"
                v-model="selectedIds"
              />
            </td>

            <td>{{ row.pablo_id }}</td>
            <td>{{ row.ord_no }}</td>
            <td>{{ row.item_no }}</td>

            <!-- Editable quantity -->
            <td>
                <input
                    type="number"
                    min="0"
                    v-model.number="row.ord_qty"
                    @blur="saveRow(row)"
                />
                </td>

                <td>
                  <select v-model="row.machine" @change="saveRow(row)">
                    <option value="" disabled>Select machine…</option>
                    <option v-for="m in machineOptions" :key="m" :value="m">
                      {{ m }}
                    </option>
                  </select>
                </td>


                

            <!-- Read-only fields -->
            <td>{{ row.sync_status }}</td>
            <td>{{ row.source }}</td>
            
          </tr>
        </tbody>
      </table>

      <div class="bulk-actions">
        <button
          @click="commitSelected"
          :disabled="selectedIds.length === 0 || committing"
        >
          {{ committing ? 'Committing…' : 'Commit Selected' }}
        </button>

        <button
          @click="deleteSelected"
          :disabled="selectedIds.length === 0 || deleting"
        >
          {{ deleting ? 'Deleting…' : 'Delete Selected' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PendingWorkOrders',

  emits: ['close', 'committed'],

  props: {
    // JIMScheduling will pass this in
    initialLocation: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      location: this.initialLocation, // F / D / K / L
      pending: [],
      loading: false,
      committing: false,
      deleting: false,
      savingRowId: null,
      deletingRowId: null,
      selectedIds: [],
      machineOptions: ['J 1','J 2','J 3','J 4','J 5','J 6','J 7','J 8', 'J 9','J 10','J 11','J 12','J 13','J 14','J 15','J 16','J 17'],
    };
  },

  created() {
    this.fetchPending();
  },

  computed: {
    allSelected() {
      return (
        this.pending.length > 0 &&
        this.selectedIds.length === this.pending.length
      );
    },
  },

  methods: {
    async fetchPending() {
      this.loading = true;
      this.selectedIds = [];
      try {
        // Use relative URL so it works in dev/prod via your proxy
        const resp = await axios.get('/pendingWorkOrders', {
          params: { loc: this.location },
        });
        this.pending = resp.data || [];
      } catch (err) {
        console.error('Error fetching pending work orders:', err);
        alert('Error fetching pending work orders.');
      } finally {
        this.loading = false;
      }
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.pending.map((r) => r.pablo_id);
      } else {
        this.selectedIds = [];
      }
    },

    async saveRow(row) {
      this.savingRowId = row.pablo_id;
      try {
        await axios.put(`/pendingWorkOrders/${row.pablo_id}`, {
          ord_qty: row.ord_qty,
          machine: row.machine,
          due_dt: row.due_dt,
          // you can add start_dt / rel_dt here later if you want
        });
        // assuming local state is now truth; no refetch needed
      } catch (err) {
        console.error('Error saving row:', err);
        alert(`Error saving work order ${row.pablo_id}`);
      } finally {
        this.savingRowId = null;
      }
    },

    async deleteRow(row) {
      if (!confirm(`Delete work order ${row.pablo_id}?`)) return;

      this.deletingRowId = row.pablo_id;
      try {
        await axios.delete('/pendingWorkOrders', {
          data: { ids: [row.pablo_id] },
        });

        this.pending = this.pending.filter(
          (r) => r.pablo_id !== row.pablo_id
        );
        this.selectedIds = this.selectedIds.filter(
          (id) => id !== row.pablo_id
        );
      } catch (err) {
        console.error('Error deleting row:', err);
        alert(`Error deleting work order ${row.pablo_id}`);
      } finally {
        this.deletingRowId = null;
      }
    },

    async deleteSelected() {
      if (this.selectedIds.length === 0) return;
      if (!confirm(`Delete ${this.selectedIds.length} pending work order(s)?`)) return;

      const loc = String(this.initialLocation || '')
        .trim()
        .toUpperCase()
        .slice(0, 1);

      this.deleting = true;
      try {
        await axios.delete('/pendingWorkOrders', {
          data: {
            ids: this.selectedIds,
            loc
          }
        });

        this.pending = this.pending.filter(
          (r) => !this.selectedIds.includes(r.pablo_id)
        );
        this.selectedIds = [];
      } catch (err) {
        console.error('Error deleting selected:', err.response?.data || err.message);
        alert('Error deleting selected work orders.');
      } finally {
        this.deleting = false;
      }
    },

    async commitSelected() {
      if (this.selectedIds.length === 0) return;
      if (
        !confirm(
          `Commit ${this.selectedIds.length} work order(s) to Macola?`
        )
      ) {
        return;
      }
      
      this.committing = true;
      try {

        const loc = String(this.initialLocation || '')
        .trim()
        .toUpperCase()
        .slice(0, 1);
        const resp = await axios.post('/pendingWorkOrders/commit', {
          ids: this.selectedIds,
          loc
        });

        const committedIds = resp.data.committedIds || [];

        this.pending = this.pending.filter(
          (r) => !committedIds.includes(r.pablo_id)
        );

        this.selectedIds = this.selectedIds.filter(
          (id) => !committedIds.includes(id)
        );

        // Tell JIMScheduling that committed rows changed status/source.
        // The parent will refresh the visible production schedule.
        if (committedIds.length > 0) {
          this.$emit('committed', {
            committedIds,
            committedCount: committedIds.length
          });
        }
      } catch (err) {
        console.error('Error committing selected:', err);
        alert('Error committing selected work orders.');
      } finally {
        this.committing = false;
      }
    },
  },
};
</script>

<style scoped>
/* Reuse your JIMScheduling base look */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #cbbfbf;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #0b0101;
  min-height: 100%;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
}

.controls {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.status-msg {
  margin-top: 10px;
}

/* Table styling */
.table-wrapper {
  width: 95%;
  max-width: 1200px;
  overflow-x: auto;
}

.pending-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #ffffff;
  color: #2c3e50;
  border-radius: 8px;
  overflow: hidden;
}

.pending-table th,
.pending-table td {
  padding: 8px 10px;
  border: 1px solid #ddd;
  text-align: left;
  font-size: 14px;
}

.pending-table th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.pending-table input[type='number'],
.pending-table input[type='text'],
.pending-table input[type='date'] {
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  padding: 2px 4px;
}

.bulk-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  justify-content: center;
}

button {
  margin-top: 0;
  padding: 6px 12px;
  cursor: pointer;
}

.close-btn {
  background-color: #888;
  color: #fff;
}
</style>
